import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import {
  Bell,
  Mail,
  Server,
  Settings,
  Send,
  Loader2,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Plus,
  Edit2,
  Trash2,
  FileText,
  Users,
  AlertTriangle,
  Save,
  TestTube,
  Copy,
  RefreshCw,
  Link,
  Unlink,
  ExternalLink
} from "lucide-react";

export default function NotificacionesConfigPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const { getAuthHeaders } = useAuth();

  // SMTP Config
  const [smtpConfig, setSmtpConfig] = useState({
    enabled: false,
    smtp_server: "smtp.office365.com",
    smtp_port: 587,
    use_tls: true,
    username: "",
    password: "",
    from_name: "",
    from_email: "",
    default_cc: [],
    default_bcc: [],
    system_emails_enabled: true
  });

  // M365 OAuth Config
  const [m365Config, setM365Config] = useState({
    client_id: "",
    client_secret: "",
    tenant_id: "",
    redirect_uri: ""
  });
  const [m365Status, setM365Status] = useState({
    configured: false,
    connected: false,
    user_email: null,
    error: null
  });
  const [m365Connecting, setM365Connecting] = useState(false);
  const [expectedRedirectUri, setExpectedRedirectUri] = useState("");
  const [m365Error, setM365Error] = useState(null);

  // Notification Events
  const [events, setEvents] = useState([]);

  // Email Templates
  const [templates, setTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    subject: "",
    body_html: "",
    variables: []
  });

  // CC/BCC inputs
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/config/email`, getAuthHeaders());
      if (res.data.smtp) {
        setSmtpConfig(prev => ({ ...prev, ...res.data.smtp }));
        setCcInput(res.data.smtp.default_cc?.join(", ") || "");
        setBccInput(res.data.smtp.default_bcc?.join(", ") || "");
      }
      if (res.data.events) setEvents(res.data.events);
      if (res.data.templates) setTemplates(res.data.templates);
      
      // Fetch M365 config
      try {
        const m365Res = await axios.get(`${API}/config/email/m365`, getAuthHeaders());
        setM365Status(m365Res.data);
      } catch (e) {
        console.log("M365 config not available");
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveSMTPConfig = async () => {
    setSaving(true);
    try {
      // Parse CC/BCC from comma-separated strings
      const configToSave = {
        ...smtpConfig,
        default_cc: ccInput ? ccInput.split(",").map(e => e.trim()).filter(e => e) : [],
        default_bcc: bccInput ? bccInput.split(",").map(e => e.trim()).filter(e => e) : []
      };

      await axios.post(`${API}/config/email/smtp`, configToSave, getAuthHeaders());
      toast.success("Configuración SMTP guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/config/email/test`, {}, getAuthHeaders());
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al probar conexión");
    } finally {
      setTesting(false);
    }
  };

  const saveEvents = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/config/email/events`, events, getAuthHeaders());
      toast.success("Eventos actualizados");
    } catch (err) {
      toast.error("Error al guardar eventos");
    } finally {
      setSaving(false);
    }
  };

  const toggleEvent = (eventId, field) => {
    setEvents(prev => prev.map(e => 
      e.event_id === eventId ? { ...e, [field]: !e[field] } : e
    ));
  };

  const updateEventField = (eventId, field, value) => {
    setEvents(prev => prev.map(e => 
      e.event_id === eventId ? { ...e, [field]: value } : e
    ));
  };

  const openNewTemplateDialog = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: "",
      subject: "",
      body_html: "",
      variables: []
    });
    setShowTemplateDialog(true);
  };

  const openEditTemplateDialog = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body_html: template.body_html,
      variables: template.variables || []
    });
    setShowTemplateDialog(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject) {
      toast.error("Nombre y asunto son requeridos");
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/config/email/templates/${editingTemplate.id}`, templateForm, getAuthHeaders());
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(`${API}/config/email/templates`, templateForm, getAuthHeaders());
        toast.success("Plantilla creada");
      }
      setShowTemplateDialog(false);
      fetchConfig();
    } catch (err) {
      toast.error("Error al guardar plantilla");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm("¿Eliminar esta plantilla?")) return;
    try {
      await axios.delete(`${API}/config/email/templates/${templateId}`, getAuthHeaders());
      toast.success("Plantilla eliminada");
      fetchConfig();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const insertVariable = (variable) => {
    setTemplateForm(prev => ({
      ...prev,
      body_html: prev.body_html + `{{${variable}}}`
    }));
  };

  // M365 Functions
  const saveM365Config = async () => {
    if (!m365Config.client_id || !m365Config.tenant_id) {
      toast.error("Client ID y Tenant ID son requeridos");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/config/email/m365`, m365Config, getAuthHeaders());
      toast.success("Configuración M365 guardada");
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const connectM365 = async () => {
    setM365Connecting(true);
    setM365Error(null);
    try {
      const res = await axios.get(`${API}/config/email/m365/auth-url`, getAuthHeaders());
      // Store state in sessionStorage for callback verification
      sessionStorage.setItem("m365_auth_state", res.data.state);
      // Show the redirect URI being used
      setExpectedRedirectUri(res.data.redirect_uri);
      // Redirect to Microsoft login
      window.location.href = res.data.auth_url;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error al iniciar conexión con Microsoft";
      setM365Error(errorMsg);
      toast.error(errorMsg);
      setM365Connecting(false);
    }
  };

  const disconnectM365 = async () => {
    if (!window.confirm("¿Desconectar la cuenta de Microsoft 365?")) return;
    
    try {
      await axios.delete(`${API}/config/email/m365/disconnect`, getAuthHeaders());
      toast.success("Cuenta desconectada");
      setM365Status({ configured: true, connected: false, user_email: null });
    } catch (err) {
      toast.error("Error al desconectar");
    }
  };

  const testM365 = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/config/email/m365/test`, {}, getAuthHeaders());
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message || "Error al enviar correo de prueba");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar correo de prueba");
    } finally {
      setTesting(false);
    }
  };

  // Handle M365 OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");
    const error = urlParams.get("error");
    const errorDescription = urlParams.get("error_description");
    
    // Handle OAuth error from Microsoft
    if (error) {
      let errorMsg = "Error de autenticación con Microsoft";
      if (error === "access_denied") {
        errorMsg = "Acceso denegado. El usuario canceló la autorización o no tiene permisos suficientes.";
      } else if (error === "invalid_request") {
        errorMsg = "Solicitud inválida. Verifique la configuración de Azure AD.";
      } else if (errorDescription) {
        errorMsg = decodeURIComponent(errorDescription.replace(/\+/g, ' '));
      }
      setM365Error(errorMsg);
      toast.error(errorMsg);
      sessionStorage.removeItem("m365_auth_state");
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    if (code && state) {
      const savedState = sessionStorage.getItem("m365_auth_state");
      if (state === savedState) {
        // Exchange code for tokens
        const exchangeToken = async () => {
          setM365Connecting(true);
          setM365Error(null);
          try {
            const res = await axios.post(
              `${API}/config/email/m365/callback`,
              null,
              {
                ...getAuthHeaders(),
                params: { code, state }
              }
            );
            
            if (res.data.success) {
              toast.success(`¡Conectado exitosamente como ${res.data.user_email}!`);
              setM365Status({
                configured: true,
                connected: true,
                user_email: res.data.user_email,
                error: null
              });
              setM365Error(null);
            }
          } catch (err) {
            const errorMsg = err.response?.data?.detail || "Error al completar la conexión con Microsoft";
            setM365Error(errorMsg);
            toast.error(errorMsg);
          } finally {
            setM365Connecting(false);
            sessionStorage.removeItem("m365_auth_state");
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        };
        
        exchangeToken();
      } else {
        setM365Error("Estado de autorización inválido. Por favor, intente conectar de nuevo.");
        sessionStorage.removeItem("m365_auth_state");
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [getAuthHeaders]);

  const recipientTypes = [
    { value: "creator", label: "Creador del registro" },
    { value: "assigned", label: "Usuario asignado" },
    { value: "admin", label: "Administradores" },
    { value: "custom", label: "Destinatarios personalizados" }
  ];

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />

      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="notificaciones-config-page">
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#0C314C] rounded-xl flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Configuración de Notificaciones
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Configura el envío de correos electrónicos y notificaciones del sistema
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            className="mt-3"
            onClick={() => { window.location.href = "/configuracion/notificaciones-push"; }}
            data-testid="ir-notificaciones-push-btn"
          >
            <Bell className="w-4 h-4 mr-2" />
            Notificaciones Push del Navegador
          </Button>
        </div>

        <Tabs defaultValue="m365" className="space-y-6">
          <TabsList className="grid w-full max-w-3xl grid-cols-5">
            <TabsTrigger value="m365" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Microsoft 365
            </TabsTrigger>
            <TabsTrigger value="smtp" className="flex items-center gap-2">
              <Server className="w-4 h-4" />
              SMTP
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Eventos
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Plantillas
            </TabsTrigger>
            <TabsTrigger value="recipients" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Destinatarios
            </TabsTrigger>
          </TabsList>

          {/* Microsoft 365 OAuth Tab */}
          <TabsContent value="m365">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 dark:text-white">
                      <div className="w-8 h-8 bg-[#0078d4] rounded flex items-center justify-center">
                        <Mail className="w-4 h-4 text-white" />
                      </div>
                      Microsoft 365 OAuth2
                    </CardTitle>
                    <CardDescription className="dark:text-slate-400">
                      Conecte su cuenta corporativa de Microsoft 365 para enviar correos
                    </CardDescription>
                  </div>
                  {m365Status.connected && (
                    <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Conectado
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Connection Status */}
                {m365Status.connected ? (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <p className="font-medium text-green-800 dark:text-green-300">Cuenta Conectada</p>
                          <p className="text-sm text-green-700 dark:text-green-400">{m365Status.user_email}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={testM365}
                          disabled={testing}
                          variant="outline"
                          size="sm"
                          className="border-green-300 text-green-700 hover:bg-green-100"
                          data-testid="test-m365-btn"
                        >
                          {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TestTube className="w-4 h-4 mr-2" />}
                          Probar Envío
                        </Button>
                        <Button
                          onClick={disconnectM365}
                          variant="outline"
                          size="sm"
                          className="border-red-300 text-red-700 hover:bg-red-100"
                          data-testid="disconnect-m365-btn"
                        >
                          <Unlink className="w-4 h-4 mr-2" />
                          Desconectar
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : m365Status.user_email ? (
                  /* Token expired but was previously connected */
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-800 rounded-full flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                          <p className="font-medium text-amber-800 dark:text-amber-300">Sesión Expirada</p>
                          <p className="text-sm text-amber-700 dark:text-amber-400">
                            Última cuenta: {m365Status.user_email}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={connectM365}
                          disabled={m365Connecting}
                          className="bg-[#0078d4] hover:bg-[#106ebe] text-white"
                          data-testid="reconnect-m365-btn"
                        >
                          {m365Connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                          Reconectar
                        </Button>
                        <Button
                          onClick={disconnectM365}
                          variant="outline"
                          size="sm"
                          className="border-red-300 text-red-700 hover:bg-red-100"
                          data-testid="disconnect-m365-btn"
                        >
                          <Unlink className="w-4 h-4 mr-2" />
                          Desconectar
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Error Message Display */}
                    {m365Error && (
                      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-red-100 dark:bg-red-800 rounded-full flex items-center justify-center flex-shrink-0">
                            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-red-800 dark:text-red-300">Error de Conexión</p>
                            <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                              {m365Error}
                            </p>
                            <Button
                              onClick={() => setM365Error(null)}
                              variant="ghost"
                              size="sm"
                              className="mt-2 text-red-600 hover:text-red-800 hover:bg-red-100 p-0 h-auto"
                            >
                              Descartar mensaje
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Connecting State */}
                    {m365Connecting && (
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg mb-4">
                        <div className="flex items-center gap-3">
                          <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                          <div>
                            <p className="font-medium text-blue-800 dark:text-blue-300">Conectando con Microsoft...</p>
                            <p className="text-sm text-blue-700 dark:text-blue-400">
                              Procesando la autorización. Por favor espere.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Azure AD Setup Info */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        Configuración en Azure AD
                      </h4>
                      <ol className="text-sm text-blue-700 dark:text-blue-400 space-y-2 list-decimal list-inside">
                        <li>Vaya a <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="underline font-medium">Azure Portal → App registrations</a></li>
                        <li>Cree una nueva aplicación o use una existente</li>
                        <li>En <strong>API permissions</strong>, agregue:
                          <ul className="ml-5 mt-1 list-disc">
                            <li><code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">Mail.Send</code> (Delegated)</li>
                            <li><code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">User.Read</code> (Delegated)</li>
                          </ul>
                        </li>
                        <li>En <strong>Certificates & secrets</strong>, cree un Client Secret</li>
                        <li>En <strong>Authentication</strong>, agregue esta URI de redirección exacta:
                          <div className="mt-2 p-2 bg-blue-100 dark:bg-blue-800 rounded flex items-center gap-2">
                            <code className="text-xs break-all flex-1">
                              {window.location.origin}/configuracion/notificaciones/m365-callback
                            </code>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 px-2"
                              onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/configuracion/notificaciones/m365-callback`);
                                toast.success("URI copiada");
                              }}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-xs mt-1 text-blue-600 dark:text-blue-300">
                            ⚠️ Esta URI debe coincidir EXACTAMENTE con la registrada en Azure AD
                          </p>
                        </li>
                        <li>Un administrador debe otorgar consentimiento para la organización</li>
                      </ol>
                    </div>

                    {/* Azure AD Credentials Form */}
                    <div className="space-y-4">
                      <h4 className="font-medium dark:text-white flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#0C314C]" />
                        Credenciales de Azure AD
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="dark:text-slate-300">Application (Client) ID *</Label>
                          <Input
                            value={m365Config.client_id}
                            onChange={(e) => setM365Config(prev => ({ ...prev, client_id: e.target.value }))}
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            className="dark:bg-slate-700 dark:border-slate-600 font-mono text-sm"
                            data-testid="m365-client-id"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="dark:text-slate-300">Directory (Tenant) ID *</Label>
                          <Input
                            value={m365Config.tenant_id}
                            onChange={(e) => setM365Config(prev => ({ ...prev, tenant_id: e.target.value }))}
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            className="dark:bg-slate-700 dark:border-slate-600 font-mono text-sm"
                            data-testid="m365-tenant-id"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="dark:text-slate-300">Client Secret *</Label>
                        <div className="relative">
                          <Input
                            type={showClientSecret ? "text" : "password"}
                            value={m365Config.client_secret}
                            onChange={(e) => setM365Config(prev => ({ ...prev, client_secret: e.target.value }))}
                            placeholder="Ingrese el Client Secret"
                            className="dark:bg-slate-700 dark:border-slate-600 pr-10"
                            data-testid="m365-client-secret"
                          />
                          <button
                            type="button"
                            onClick={() => setShowClientSecret(!showClientSecret)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            {showClientSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-slate-500">
                          Se encuentra en Azure AD → App registrations → Su app → Certificates & secrets
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label className="dark:text-slate-300">
                          URI de Redirección (opcional)
                          <span className="text-xs text-slate-400 ml-2">Solo si la automática no funciona</span>
                        </Label>
                        <Input
                          value={m365Config.redirect_uri}
                          onChange={(e) => setM365Config(prev => ({ ...prev, redirect_uri: e.target.value }))}
                          placeholder={`${window.location.origin}/configuracion/notificaciones/m365-callback`}
                          className="dark:bg-slate-700 dark:border-slate-600 font-mono text-sm"
                          data-testid="m365-redirect-uri"
                        />
                        <p className="text-xs text-slate-500">
                          Déjelo vacío para usar la URI automática. Solo especifique si tiene una URI diferente registrada en Azure AD.
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-4 pt-4 border-t dark:border-slate-700">
                      <Button
                        onClick={saveM365Config}
                        disabled={saving || !m365Config.client_id || !m365Config.tenant_id || !m365Config.client_secret}
                        variant="outline"
                        data-testid="save-m365-config"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        Guardar Credenciales
                      </Button>
                      <Button
                        onClick={connectM365}
                        disabled={m365Connecting || !m365Config.client_id || !m365Config.tenant_id}
                        className="bg-[#0078d4] hover:bg-[#106ebe] text-white"
                        data-testid="connect-m365"
                      >
                        {m365Connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link className="w-4 h-4 mr-2" />}
                        Conectar con Microsoft
                      </Button>
                    </div>
                  </>
                )}

                {/* Benefits info */}
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg mt-4">
                  <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Ventajas de Microsoft 365 OAuth2:</h4>
                  <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1 list-disc list-inside">
                    <li>No requiere habilitar SMTP AUTH en el buzón</li>
                    <li>Funciona con MFA (autenticación multifactor) habilitado</li>
                    <li>No necesita contraseñas de aplicación</li>
                    <li>Los correos se guardan en "Elementos enviados"</li>
                    <li>Más seguro que autenticación básica SMTP</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMTP Configuration Tab */}
          <TabsContent value="smtp">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 dark:text-white">
                      <Mail className="w-5 h-5 text-[#0C314C]" />
                      Configuración SMTP - Microsoft 365
                    </CardTitle>
                    <CardDescription className="dark:text-slate-400">
                      Configure los parámetros de conexión para envío de correos
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm dark:text-slate-300">Habilitar</Label>
                    <Switch
                      checked={smtpConfig.enabled}
                      onCheckedChange={(v) => setSmtpConfig(prev => ({ ...prev, enabled: v }))}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* M365 Requirements Info */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">Requisitos para Microsoft 365:</h4>
                  <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-disc list-inside">
                    <li><strong>SMTP AUTH debe estar habilitado</strong> para el usuario en el Centro de Administración de Exchange</li>
                    <li>Si tiene <strong>MFA habilitado</strong>, debe crear una "App Password" en su cuenta Microsoft</li>
                    <li>El <strong>email del remitente</strong> debe ser igual al usuario o un alias autorizado</li>
                    <li>Puerto 587 con TLS es el estándar para M365</li>
                  </ul>
                  <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                    <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">¿Cómo habilitar SMTP AUTH?</p>
                    <ol className="text-xs text-blue-600 dark:text-blue-400 list-decimal list-inside space-y-0.5">
                      <li>Ir a <strong>Centro de administración de Microsoft 365</strong></li>
                      <li>Usuarios → Usuarios activos → Seleccionar el usuario</li>
                      <li>Pestaña "Correo" → "Administrar aplicaciones de correo electrónico"</li>
                      <li>Habilitar <strong>"SMTP autenticado"</strong></li>
                    </ol>
                  </div>
                </div>

                {/* System Emails Toggle */}
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-amber-800 dark:text-amber-300">Correos Automáticos del Sistema</h4>
                      <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                        Habilita o deshabilita los correos automáticos del sistema (notificaciones de aprobación de permisos, alertas, etc.). 
                        Los correos configurados en "Plantillas de Correos" siempre se enviarán independientemente de esta configuración.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Label className="text-sm text-amber-800 dark:text-amber-300">
                        {smtpConfig.system_emails_enabled ? 'Activo' : 'Desactivado'}
                      </Label>
                      <Switch
                        checked={smtpConfig.system_emails_enabled}
                        onCheckedChange={(v) => setSmtpConfig(prev => ({ ...prev, system_emails_enabled: v }))}
                        data-testid="system-emails-toggle"
                      />
                    </div>
                  </div>
                </div>

                {/* Server Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">Servidor SMTP</Label>
                    <Input
                      value={smtpConfig.smtp_server}
                      onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_server: e.target.value }))}
                      placeholder="smtp.office365.com"
                      className="dark:bg-slate-700 dark:border-slate-600"
                      data-testid="smtp-server-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">Puerto</Label>
                    <Input
                      type="number"
                      value={smtpConfig.smtp_port}
                      onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                      className="dark:bg-slate-700 dark:border-slate-600"
                      data-testid="smtp-port-input"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={smtpConfig.use_tls}
                    onCheckedChange={(v) => setSmtpConfig(prev => ({ ...prev, use_tls: v }))}
                  />
                  <Label className="dark:text-slate-300">Usar TLS (recomendado para M365)</Label>
                </div>

                <Separator />

                {/* Credentials */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">Usuario / Email</Label>
                    <Input
                      type="email"
                      value={smtpConfig.username}
                      onChange={(e) => setSmtpConfig(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="usuario@dominio.com"
                      className="dark:bg-slate-700 dark:border-slate-600"
                      data-testid="smtp-username-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">Contraseña / App Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={smtpConfig.password}
                        onChange={(e) => setSmtpConfig(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="••••••••"
                        className="dark:bg-slate-700 dark:border-slate-600 pr-10"
                        data-testid="smtp-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">
                      Si tiene MFA: Vaya a <strong>account.microsoft.com</strong> → Seguridad → Contraseñas de aplicación → Crear nueva
                    </p>
                  </div>
                </div>

                <Separator />

                {/* From Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">Nombre del Remitente (predeterminado)</Label>
                    <Input
                      value={smtpConfig.from_name}
                      onChange={(e) => setSmtpConfig(prev => ({ ...prev, from_name: e.target.value }))}
                      placeholder="Sistema de Gestión"
                      className="dark:bg-slate-700 dark:border-slate-600"
                      data-testid="smtp-from-name-input"
                    />
                    <p className="text-xs text-amber-600">
                      <strong>Importante:</strong> Microsoft 365 puede ignorar este nombre y usar el de la cuenta de correo. 
                      Para personalizar el remitente en Marketing, se debe configurar directamente en el Centro de Administración de Exchange de Microsoft 365.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">Email del Remitente</Label>
                    <Input
                      type="email"
                      value={smtpConfig.from_email}
                      onChange={(e) => setSmtpConfig(prev => ({ ...prev, from_email: e.target.value }))}
                      placeholder="notificaciones@dominio.com"
                      className="dark:bg-slate-700 dark:border-slate-600"
                      data-testid="smtp-from-email-input"
                    />
                    <p className="text-xs text-slate-500">
                      Debe ser igual al usuario o un alias autorizado
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-4 pt-4">
                  <Button
                    onClick={saveSMTPConfig}
                    disabled={saving}
                    className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                    data-testid="save-smtp-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Guardar Configuración
                  </Button>
                  <Button
                    onClick={testConnection}
                    disabled={testing || !smtpConfig.enabled}
                    variant="outline"
                    data-testid="test-connection-btn"
                  >
                    {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TestTube className="w-4 h-4 mr-2" />}
                    Probar Conexión
                  </Button>
                </div>

                {!smtpConfig.enabled && (
                  <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm text-yellow-700 dark:text-yellow-400">
                      Las notificaciones por correo están deshabilitadas
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 dark:text-white">
                      <Bell className="w-5 h-5 text-[#0C314C]" />
                      Eventos de Notificación
                    </CardTitle>
                    <CardDescription className="dark:text-slate-400">
                      Configure qué eventos generan notificaciones por correo
                    </CardDescription>
                  </div>
                  <Button
                    onClick={saveEvents}
                    disabled={saving}
                    className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                    data-testid="save-events-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Guardar Cambios
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {events.map((event) => (
                    <div
                      key={event.event_id}
                      className="flex items-center justify-between p-4 border rounded-lg dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                    >
                      <div className="flex items-center gap-4">
                        <Switch
                          checked={event.enabled}
                          onCheckedChange={() => toggleEvent(event.event_id, "enabled")}
                        />
                        <div>
                          <p className="font-medium dark:text-white">{event.event_name}</p>
                          <p className="text-sm text-slate-500">ID: {event.event_id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-48">
                          <Select
                            value={event.recipients_type}
                            onValueChange={(v) => updateEventField(event.event_id, "recipients_type", v)}
                          >
                            <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {recipientTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-48">
                          <Select
                            value={event.template_id || "default"}
                            onValueChange={(v) => updateEventField(event.event_id, "template_id", v === "default" ? null : v)}
                          >
                            <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                              <SelectValue placeholder="Plantilla" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">Plantilla por defecto</SelectItem>
                              {templates.map((tmpl) => (
                                <SelectItem key={tmpl.id} value={tmpl.id}>
                                  {tmpl.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Badge className={event.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                          {event.enabled ? "Activo" : "Inactivo"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 dark:text-white">
                      <FileText className="w-5 h-5 text-[#0C314C]" />
                      Plantillas de Correo
                    </CardTitle>
                    <CardDescription className="dark:text-slate-400">
                      Personaliza el contenido de los correos de notificación
                    </CardDescription>
                  </div>
                  <Button
                    onClick={openNewTemplateDialog}
                    className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                    data-testid="new-template-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Plantilla
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {templates.map((template) => (
                    <Card key={template.id} className="dark:bg-slate-700 dark:border-slate-600">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-base dark:text-white">{template.name}</CardTitle>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                              Asunto: {template.subject}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditTemplateDialog(template)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteTemplate(template.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-2">
                        <div className="flex flex-wrap gap-1">
                          {template.variables?.map((v) => (
                            <Badge key={v} variant="outline" className="text-xs">
                              {`{{${v}}}`}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recipients Tab */}
          <TabsContent value="recipients">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Users className="w-5 h-5 text-[#0C314C]" />
                  Destinatarios por Defecto
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Configure destinatarios CC y BCC que se incluirán en todas las notificaciones
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">CC (Copia)</Label>
                  <Textarea
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    placeholder="correo1@ejemplo.com, correo2@ejemplo.com"
                    className="dark:bg-slate-700 dark:border-slate-600"
                    rows={2}
                  />
                  <p className="text-xs text-slate-500">
                    Separe múltiples correos con comas. Estos destinatarios recibirán una copia visible.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="dark:text-slate-300">BCC (Copia Oculta)</Label>
                  <Textarea
                    value={bccInput}
                    onChange={(e) => setBccInput(e.target.value)}
                    placeholder="supervisor@ejemplo.com, auditoria@ejemplo.com"
                    className="dark:bg-slate-700 dark:border-slate-600"
                    rows={2}
                  />
                  <p className="text-xs text-slate-500">
                    Separe múltiples correos con comas. Estos destinatarios recibirán una copia oculta.
                  </p>
                </div>

                <Button
                  onClick={saveSMTPConfig}
                  disabled={saving}
                  className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar Destinatarios
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Template Dialog */}
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 dark:text-white">
                <FileText className="w-5 h-5 text-[#0C314C]" />
                {editingTemplate ? "Editar Plantilla" : "Nueva Plantilla"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="dark:text-slate-300">Nombre de la Plantilla *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Notificación de Ticket"
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>

              <div className="space-y-2">
                <Label className="dark:text-slate-300">Asunto del Correo *</Label>
                <Input
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Ej: Nuevo Ticket #{{numero}}: {{titulo}}"
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
                <p className="text-xs text-slate-500">
                  Use {"{{variable}}"} para insertar valores dinámicos
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="dark:text-slate-300">Contenido HTML</Label>
                  <div className="flex gap-1">
                    {["numero", "titulo", "nombre", "fecha", "estado", "descripcion"].map((v) => (
                      <Button
                        key={v}
                        variant="outline"
                        size="sm"
                        className="text-xs h-6"
                        onClick={() => insertVariable(v)}
                      >
                        {`{{${v}}}`}
                      </Button>
                    ))}
                  </div>
                </div>
                <Textarea
                  value={templateForm.body_html}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, body_html: e.target.value }))}
                  placeholder="<div>Contenido del correo en HTML...</div>"
                  className="dark:bg-slate-700 dark:border-slate-600 font-mono text-sm"
                  rows={12}
                />
              </div>

              <div className="space-y-2">
                <Label className="dark:text-slate-300">Variables Disponibles</Label>
                <Input
                  value={templateForm.variables?.join(", ") || ""}
                  onChange={(e) => setTemplateForm(prev => ({ 
                    ...prev, 
                    variables: e.target.value.split(",").map(v => v.trim()).filter(v => v)
                  }))}
                  placeholder="numero, titulo, nombre, fecha"
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
                <p className="text-xs text-slate-500">
                  Lista de variables separadas por comas (para documentación)
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
                Cancelar
              </Button>
              <Button
                onClick={saveTemplate}
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editingTemplate ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
